import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';

/* ───────────────────────────────────────────────────────────────────────────
   Contrato — CRUD /fiscal/difal-config (FiscalConfigController)

   Espelha `DifalConfigResponseDto` e os DTOs de create/update/query
   (presentation/http/dto/fiscal-config.dto.ts).

   Partilha padrão do DIFAL (EC 87/2015): origem 40% / destino 60% quando
   percentualPartilhaOrigem/Destino não são informados.

   Envelope paginado {data, total, page, limit, totalPages} nas listagens;
   filtros ufOrigem/ufDestino/enabled. O tenant vem da sessão.
   ─────────────────────────────────────────────────────────────────────────── */

export interface DifalConfig {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  ufOrigem: string;
  ufDestino: string;
  aliquotaInterna: number;
  aliquotaInterestadual: number;
  percentualPartilhaOrigem: number;
  percentualPartilhaDestino: number;
  fcpAliquota: number | null;
  soConsumidorFinal: boolean;
  enabled: boolean;
  effectiveSince: string;
  effectiveUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DifalConfigInput {
  name: string;
  description?: string;
  ufOrigem: string;
  ufDestino: string;
  aliquotaInterna: number;
  aliquotaInterestadual: number;
  percentualPartilhaOrigem?: number;
  percentualPartilhaDestino?: number;
  fcpAliquota?: number;
  soConsumidorFinal?: boolean;
  enabled?: boolean;
  effectiveSince: string;
  effectiveUntil?: string;
}

export interface PaginatedDifalConfigs {
  data: DifalConfig[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface DifalConfigQuery {
  ufOrigem?: string;
  ufDestino?: string;
  enabled?: boolean;
  page?: number;
  limit?: number;
}

const KEY = ['fiscal', 'difal-config'] as const;

export function useDifalConfigs(params?: DifalConfigQuery) {
  return useQuery({
    queryKey: [KEY, params] as const,
    queryFn: () =>
      apiClient.get<PaginatedDifalConfigs>('/fiscal/difal-config', {
        params: {
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 50),
          ...(params?.ufOrigem ? { ufOrigem: params.ufOrigem } : {}),
          ...(params?.ufDestino ? { ufDestino: params.ufDestino } : {}),
          ...(params?.enabled !== undefined ? { enabled: String(params.enabled) } : {}),
        },
      }),
  });
}

export function useCreateDifalConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DifalConfigInput) =>
      apiClient.post<DifalConfig>('/fiscal/difal-config', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateDifalConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: DifalConfigInput & { id: string }) =>
      apiClient.put<DifalConfig>(`/fiscal/difal-config/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteDifalConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/fiscal/difal-config/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}